import { SubmitHandler, useForm } from 'react-hook-form';
import { BUSINESS_CHALLENGES, CreateProjectContext, LANGUAGES, LanguagesType } from './consts';
import {
  Button,
  Input,
  InputDropdown,
  InputDropdownItem,
  isEmpty,
  MultiSelectDropdown,
  RichTextEditor,
  santizeInput,
} from '@platform/components';
import { ROUTES } from '../routes/consts';
import { useContext, useState } from 'react';
import {
  CreateProjectRequestSupportTypeInfoItem,
  ProjectDeliveryType,
  ProjectWorkLoad,
} from '@platform/client/schemas';
import { MyProjects, Projects } from '@platform/client';
import { FormBlock } from './utils';
import { navigate } from 'wouter/use-browser-location';
import { useQueryClient } from '@tanstack/react-query';

type FormValues = {
  languages: LanguagesType[];
  projectPhoto: string; // TODO: Correct type. Or, just remove?
  workWithTeam: boolean;
  /* Trainer */
  trainingTopicRichText: string;
  trainerAdditionalRequirements: string;
  trainerLocation: ProjectDeliveryType;
  /* Call */
  callOutcomesRichText: string;
  /* Mentor */
  mentorOutcomesRichText: string;
  /* Coach */
  coachOutcomesRichText: string;
  // TODO: the Participation Agreement blurb, which mentions EY for some reason
  /* Consultant */
  projectTitle: string;
  expectedDeliverablesRichText: string;
  consultantAdditionalRequirements: string;
  projectTimeCommitment: ProjectWorkLoad;
  projectBkgRichText: string;
  consultantLocation: ProjectDeliveryType;
  learningOutcomesRichText: string;
};

const languageOptions = LANGUAGES.map((lang) => ({
  label: lang,
  value: lang,
}));

const locationOptions: InputDropdownItem[] = [
  {
    text: 'In-Person',
    textValue: ProjectDeliveryType.onsite,
  },
  {
    text: 'Virtual',
    textValue: ProjectDeliveryType.remote,
  },
  {
    text: 'Hybrid',
    textValue: ProjectDeliveryType.hybrid,
  },
];

const FinalDetailsStep = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const queryClient = useQueryClient();
  const createProjectMutator = Projects.useCreateProject({
    mutation: {
      onMutate() {
        setIsSubmitting(true);
      },
      onError: () => {
        setIsSubmitting(false);
        // Displays a generic "something went wrong" error
        setIsSubmissionError(true);
      },
      onSuccess: () => {
        setIsSubmitting(false);
        queryClient.invalidateQueries({
          queryKey: [...MyProjects.getGetOrgUserMyProjectsQueryKey()],
        });
        navigate(`platform${ROUTES.DASHBOARD}${ROUTES.MY_PROJECTS}`);
      },
    },
  });
  const { businessChallenge, skills, supportTypes } = useContext(CreateProjectContext);

  const { setValue, watch, register, handleSubmit } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { languages: [] },
  });

  let isSubmitEnabled = true;

  // Determine if submit should be enabled by checking if all required fields for each support type are filled

  // Check general required fields
  isSubmitEnabled =
    isSubmitEnabled &&
    watch('languages') &&
    watch('languages').length > 0 &&
    watch('workWithTeam') !== undefined;

  if (supportTypes.has('Trainer')) {
    isSubmitEnabled =
      isSubmitEnabled &&
      !isEmpty(watch('trainingTopicRichText')) &&
      !isEmpty(watch('trainerLocation'));
  }
  if (supportTypes.has('Call')) {
    isSubmitEnabled = isSubmitEnabled && !isEmpty(watch('callOutcomesRichText'));
  }
  if (supportTypes.has('Mentor')) {
    isSubmitEnabled = isSubmitEnabled && !isEmpty(watch('mentorOutcomesRichText'));
  }
  if (supportTypes.has('Coach')) {
    isSubmitEnabled = isSubmitEnabled && !isEmpty(watch('coachOutcomesRichText'));
  }
  if (supportTypes.has('Consultant')) {
    isSubmitEnabled =
      isSubmitEnabled &&
      !isEmpty(watch('projectTitle')) &&
      !isEmpty(watch('expectedDeliverablesRichText')) &&
      !isEmpty(watch('projectTimeCommitment')) &&
      !isEmpty(watch('projectBkgRichText')) &&
      !isEmpty(watch('consultantLocation'));
  }

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const supportTypeAnswers: CreateProjectRequestSupportTypeInfoItem[] = [];

    if (supportTypes.has('Trainer')) {
      supportTypeAnswers.push({
        roleType: 'trainer',
        additionalRequirements: data.trainerAdditionalRequirements,
        outcomes: santizeInput(data.trainingTopicRichText),
        location: data.trainerLocation,
      });
    }

    if (supportTypes.has('Call')) {
      supportTypeAnswers.push({
        // This role type is not a bug; see the documentation for CallProjectParams
        roleType: 'consultant',
        outcomes: santizeInput(data.callOutcomesRichText),
      });
    }

    if (supportTypes.has('Mentor')) {
      supportTypeAnswers.push({
        roleType: 'mentor',
        outcomes: santizeInput(data.mentorOutcomesRichText),
      });
    }

    if (supportTypes.has('Coach')) {
      supportTypeAnswers.push({
        roleType: 'coach',
        outcomes: santizeInput(data.coachOutcomesRichText),
      });
    }

    if (supportTypes.has('Consultant')) {
      supportTypeAnswers.push({
        // This role type is not a bug; see the documentation for ConsultantProjectParams
        roleType: 'doer',
        projectTitle: data.projectTitle,
        deliverables: santizeInput(data.expectedDeliverablesRichText),
        additionalRequirements: data.consultantAdditionalRequirements ?? null,
        timeCommitment: data.projectTimeCommitment,
        projectBackground: santizeInput(data.projectBkgRichText),
        consultantLocation: data.consultantLocation,
        learningOutcomes: santizeInput(data.learningOutcomesRichText),
      });
    }

    createProjectMutator.mutate({
      data: {
        baseParams: {
          businessChallenge:
            BUSINESS_CHALLENGES.find((bc) => bc.name === businessChallenge)?.value || '',
          skills: Array.from(skills),
          languages: data.languages,
          openToTeam: data.workWithTeam,
        },
        supportTypeInfo: supportTypeAnswers,
      },
    });
  };

  return (
    <>
      <div>Please complete the following fields to finalize your submission.</div>
      <div className='w-2xl flex flex-col gap-4'>
        {/* General information, shows regardless of support type(s) selected */}
        <FormBlock header='General Information'>
          <MultiSelectDropdown
            register={() => {
              return register('languages');
            }}
            selectedItems={watch('languages') || []}
            onSelectionChange={(v) => setValue('languages', v as LanguagesType[])}
            options={languageOptions}
            placeholder='Select one or more languages'
            label='What languages would an experteer need to complete the project(s)?'
            filterable
          />
          <InputDropdown
            items={[
              {
                text: 'Yes',
                textValue: 'yes',
              },
              {
                text: 'No',
                textValue: 'no',
              },
            ]}
            label='Would you be open to working with a team of experteers on this project?'
            // TODO: Help icon & popover
            // hintText='In some of our corporate pro bono programs, employees volunteer their skills in teams. That means you get support from multiple professionals with different skill sets who work together to support your business challenges.'
            inputDisabled
            type='default'
            selectedKey={
              !watch('workWithTeam') ? undefined
              : watch('workWithTeam') ?
                'yes'
              : 'no'
            }
            onSelectionChange={(key) => {
              setValue('workWithTeam', key === 'yes');
            }}
          />
        </FormBlock>
        {/* Trainer */}
        {supportTypes.has('Trainer') && (
          <FormBlock header='Final Details For Your Trainer Request'>
            <RichTextEditor
              label='What training topic would help you and your team?'
              value={watch('trainingTopicRichText') || ''}
              onChange={(e) => {
                setValue('trainingTopicRichText', e.target.value);
              }}
              dataTestId='training-topic-rte'
            />
            <Input
              type='inline'
              label='Please list any additional requirements (Optional)'
              value={watch('trainerAdditionalRequirements') || ''}
              onChange={(e) => {
                setValue('trainerAdditionalRequirements', e.target.value);
              }}
            />
            <InputDropdown
              items={locationOptions}
              label=' Where will the project take place?'
              onSelectionChange={(key) => {
                setValue('trainerLocation', key as FormValues['trainerLocation']);
              }}
              inputDisabled
              type='default'
            />
          </FormBlock>
        )}
        {/* Call */}
        {supportTypes.has('Call') && (
          <FormBlock header='Final Details For Your Call Request'>
            <RichTextEditor
              label='What are you hoping to get out of an expert advice session?'
              value={watch('callOutcomesRichText') || ''}
              onChange={(e) => {
                setValue('callOutcomesRichText', e.target.value);
              }}
              dataTestId='call-outcomes-rte'
            />
          </FormBlock>
        )}
        {/* Mentor */}
        {supportTypes.has('Mentor') && (
          <FormBlock header='Final Details For Your Mentor Request'>
            <RichTextEditor
              label='What are you hoping to get out of a mentor?'
              value={watch('mentorOutcomesRichText') || ''}
              onChange={(value) => {
                setValue('mentorOutcomesRichText', value.target.value);
              }}
              dataTestId='mentor-outcomes-rte'
            />
          </FormBlock>
        )}
        {/* Coach */}
        {supportTypes.has('Coach') && (
          <FormBlock header='Final Details For Your Coach Request'>
            <RichTextEditor
              label='What are you hoping to get out of a coach?'
              value={watch('coachOutcomesRichText') || ''}
              onChange={(value) => {
                setValue('coachOutcomesRichText', value.target.value);
              }}
              dataTestId='coach-outcomes-rte'
            />
          </FormBlock>
        )}
        {/* Consultant */}
        {supportTypes.has('Consultant') && (
          <FormBlock header='Final Details For Your Consultant Request'>
            <Input
              type='inline'
              label='Project Title'
              value={watch('projectTitle') || ''}
              placeholder='Please enter a project title'
              onChange={(e) => {
                setValue('projectTitle', e.target.value);
              }}
            />
            <RichTextEditor
              label='What are the expected deliverables?'
              value={watch('expectedDeliverablesRichText') || ''}
              onChange={(value) => {
                setValue('expectedDeliverablesRichText', value.target.value);
              }}
              dataTestId='expected-deliverables-rte'
            />
            <Input
              type='inline'
              label='Please list any additional requirements'
              value={watch('consultantAdditionalRequirements') || ''}
              onChange={(e) => {
                setValue('consultantAdditionalRequirements', e.target.value);
              }}
            />
            <InputDropdown
              items={[
                {
                  text: 'Full-Time',
                  textValue: ProjectWorkLoad.fulltime,
                },
                {
                  text: 'Part-Time',
                  textValue: ProjectWorkLoad.parttime,
                },
                {
                  text: 'Flextime',
                  textValue: ProjectWorkLoad.flex,
                },
              ]}
              label='What is your ideal project time commitment?'
              onSelectionChange={(key) => {
                setValue('projectTimeCommitment', key as FormValues['projectTimeCommitment']);
              }}
              inputDisabled
              type='default'
            />
            <RichTextEditor
              label='Explain the background of the project and why your organization needs support'
              value={watch('projectBkgRichText') || ''}
              onChange={(value) => {
                setValue('projectBkgRichText', value.target.value);
              }}
              dataTestId='project-bkg-rte'
            />
            <InputDropdown
              items={locationOptions}
              label='Where will the project take place?'
              onSelectionChange={(key) => {
                setValue('consultantLocation', key as FormValues['consultantLocation']);
              }}
              inputDisabled
              type='default'
            />
            <RichTextEditor
              label='What can the experteer learn by working with you? (Optional)' // TODO: Figure out how we should indicate optional vs required
              value={watch('learningOutcomesRichText') || ''}
              onChange={(value) => {
                setValue('learningOutcomesRichText', value.target.value);
              }}
              dataTestId='learning-outcomes-rte'
            />
          </FormBlock>
        )}
        <div className='w-fit mx-auto'>
          <Button
            variant='accent'
            size='lg'
            onPress={() => handleSubmit(onSubmit)()}
            isDisabled={!isSubmitEnabled || isSubmitting}
          >
            Submit for Review
          </Button>
        </div>
        {isSubmissionError && (
          <div className='text-center text-red-600'>
            There was an error submitting your project. Please try again or contact support.
          </div>
        )}
        <div className='mx-auto blue-links'>
          <a href='mailto:support@movingworlds.org'>Contact MovingWorlds</a> if you have questions
          or need support.
        </div>
      </div>
    </>
  );
};

export default FinalDetailsStep;
